import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import { INSPECTIONS_GENERIC_FILTER } from "shared/filterDefinitions";

import { SchemaEntry } from "features/ui/MaterialTable";

import { VEHICLE_ACCESSOR } from "./constants";
import { SchemaEntryOverride, SchemaRelations, UseSchema } from "./types";
import { getGroupBySelectOptions } from "./utils/group-by-utils";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";
import useVehiclesSchema from "./vehiclesSchema";

export const useInspectionsSchema = (
  accessorsToHide: string[] = []
): UseSchema => {
  const { attributes } = useAttributesContext(ContextProviderName.Inspection);

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(INSPECTIONS_GENERIC_FILTER, attributeMap);

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const overrides: SchemaEntryOverride = {
    // Do not allow filtering on "Vehicle" via UI. We use a separate query param for that.
    [VEHICLE_ACCESSOR]: {
      schemaOverride: {
        filter: undefined,
      },
    },
  };

  const schema: SchemaEntry[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    undefined,
    accessorsToHide
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  const groupByRelations: SchemaRelations = {
    "/api/v1/vehicles": useVehiclesSchema(),
  };

  const attributesForGroupBy = attributes?.filter(
    ({ ID }) => !accessorsToHide.includes(ID)
  );

  const groupBySelectOptions = getGroupBySelectOptions(
    attributesForGroupBy,
    groupByRelations
  );

  return {
    schema,
    attributes,
    groupBySelectOptions,
    getDisplayLabel,
    attributeMap,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor,
  };
};
